/* eslint-disable complexity */
import React, { useCallback } from 'react';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';
import Textbox from 'carbon-react/lib/components/textbox';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Typography from 'carbon-react/lib/components/typography';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import * as styles from './filter.scss';
import { TABLE_COLUMN, Data } from '../filter-table-types';
import { forceLocalDate } from '../../dates';

interface NumericalFilterContentProps {
  fromValue: string;
  toValue: string;
  handleFromValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  handleClearColumnFilter: () => void;
  clearEnabled: boolean;
}

interface DateFilterContentProps {
  fromDate?: Date;
  toDate?: Date;
  handleFromDateChange: (date: Date) => void;
  handleToDateChange: (date: Date) => void;
  handleSubmit: () => void;
  handleClearColumnFilter: () => void;
  clearEnabled: boolean;
}

interface TimeFilterContentProps {
  fromTime?: string;
  toTime?: string;
  handleFromTimeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleToTimeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  handleClearColumnFilter: () => void;
  clearEnabled: boolean;
}

interface SelectFilterContentProps {
  options?: { id: string; name: string }[];
  handleChange: (id: string) => void;
  value?: Record<string, boolean>;
  handleClearColumnFilter: () => void;
  clearEnabled: boolean;
}

interface TextFilterContentProps {
  handleSearchQueryChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchQuery?: string;
  filteredDisplayedData?: Data[];
  checkedFilteredData?: Record<string, boolean>;
  handleDataCheckChange: (data: Data) => void;
  activeColumn?: TABLE_COLUMN;
  handleSelectAll: () => void;
  handleUnSelectAll: () => void;
  handleClearColumnFilter: () => void;
  clearEnabled: boolean;
  minQueryLength: number;
}

export const NumericalFilterContent = ({
  fromValue,
  toValue,
  handleFromValueChange,
  handleToValueChange,
  handleSubmit,
  handleClearColumnFilter,
  clearEnabled
}: NumericalFilterContentProps) => {
  const intl = useIntl();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 8 }}>
        <FormattedMessage id="common.filter_by_range" defaultMessage="Filter by range:" />
      </div>
      <div
        className={clearEnabled ? styles.enabledLabel : styles.disabledLabel}
        onClick={handleClearColumnFilter}
        style={{ marginBottom: 16 }}
      >
        <FormattedMessage id="common.clear" defaultMessage="Clear" />
      </div>
      <Textbox
        onChange={e => {
          if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
          handleFromValueChange(e);
        }}
        id="searchText"
        value={fromValue}
        autoComplete="off"
        placeholder={intl.formatMessage({ id: 'common.from_number', defaultMessage: 'From' })}
        onKeyDown={handleKeyDown}
      />
      <Textbox
        onChange={e => {
          if (!/^-?[0-9]*$/.test(e.target.value)) {return;}
          handleToValueChange(e);
        }}
        id="searchText"
        value={toValue}
        autoComplete="off"
        placeholder={intl.formatMessage({ id: 'common.to_number', defaultMessage: 'To' })}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export const DateFilterContent = ({
  fromDate,
  toDate,
  handleFromDateChange,
  handleToDateChange,
  handleSubmit,
  handleClearColumnFilter,
  clearEnabled
}: DateFilterContentProps) => {
  const intl = useIntl();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleFromDateCarbonChange = useCallback(
    (e: DateChangeEvent) => {
      handleFromDateChange(forceLocalDate(e.target.value.rawValue || ''));
    },
    [handleFromDateChange]
  );

  const handleToDateCarbonChange = useCallback(
    (e: DateChangeEvent) => {
      handleToDateChange(forceLocalDate(e.target.value.rawValue || ''));
    },
    [handleToDateChange]
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 8 }}>
        <FormattedMessage id="common.filter_by_range" defaultMessage="Filter by range:" />
      </div>
      <div
        className={clearEnabled ? styles.enabledLabel : styles.disabledLabel}
        onClick={handleClearColumnFilter}
        style={{ marginBottom: 16 }}
      >
        <FormattedMessage id="common.clear" defaultMessage="Clear" />
      </div>
      <div className={styles.carbonDate} style={{ marginBottom: 16 }}>
        <DateInput
          id="fromDate"
          onChange={handleFromDateCarbonChange}
          value={
            fromDate
              ? intl.formatDate(fromDate, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
              : ''
          }
          onKeyDown={handleKeyDown}
          label={intl.formatMessage({ id: 'common.from_date', defaultMessage: 'From' })}
        />
      </div>
      <div className={styles.carbonDate}>
        <DateInput
          id="toDate"
          onChange={handleToDateCarbonChange}
          value={
            toDate
              ? intl.formatDate(toDate, {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
              : ''
          }
          onKeyDown={handleKeyDown}
          label={intl.formatMessage({ id: 'common.to_date', defaultMessage: 'To' })}
        />
      </div>
    </div>
  );
};

export const TimeFilterContent = ({
  fromTime,
  toTime,
  handleFromTimeChange,
  handleToTimeChange,
  handleSubmit,
  handleClearColumnFilter,
  clearEnabled
}: TimeFilterContentProps) => {
  const intl = useIntl();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 8 }}>
        <FormattedMessage id="common.filter_by_range" defaultMessage="Filter by range:" />
      </div>
      <div
        className={clearEnabled ? styles.enabledLabel : styles.disabledLabel}
        onClick={handleClearColumnFilter}
        style={{ marginBottom: 16 }}
      >
        <FormattedMessage id="common.clear" defaultMessage="Clear" />
      </div>
      <Textbox
        value={fromTime}
        onChange={handleFromTimeChange}
        placeholder={intl.formatMessage({ id: 'predict.hours_minutes_time_placeholder', defaultMessage: 'hh:mm' })}
        label={intl.formatMessage({ id: 'common.from_time', defaultMessage: 'From time' })}
        onKeyDown={handleKeyDown}
      />
      <Textbox
        value={toTime}
        onChange={handleToTimeChange}
        placeholder={intl.formatMessage({ id: 'predict.hours_minutes_time_placeholder', defaultMessage: 'hh:mm' })}
        label={intl.formatMessage({ id: 'common.to_time', defaultMessage: 'To time' })}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export const SelectFilterContent = ({
  options,
  value,
  handleChange,
  handleClearColumnFilter,
  clearEnabled
}: SelectFilterContentProps) => (
  <>
    <div
      className={clearEnabled ? styles.enabledLabel : styles.disabledLabel}
      onClick={handleClearColumnFilter}
      style={{ marginBottom: 16 }}
    >
      <FormattedMessage id="common.clear" defaultMessage="Clear" />
    </div>
    <div style={{ marginBottom: 24 }}>
      {options &&
        options.map(o => (
          <div key={o.id} className={styles.checkboxContainer} style={{ marginBottom: 8 }}>
            <Checkbox
              key={o.id}
              checked={value ? Boolean(value[o.id]) : false}
              onChange={() => handleChange(o.id)}
              label={o.name}
            />
          </div>
        ))}
    </div>
  </>
);

export const TextFilterContent = ({
  handleSearchQueryChange,
  searchQuery = '',
  filteredDisplayedData,
  checkedFilteredData,
  handleDataCheckChange,
  activeColumn,
  handleSelectAll,
  handleUnSelectAll,
  handleClearColumnFilter,
  clearEnabled,
  minQueryLength
}: TextFilterContentProps) => {
  const intl = useIntl();

  const selectAllEnabled =
    searchQuery.length >= minQueryLength && filteredDisplayedData && filteredDisplayedData.length > 0;

  const isSelectedAll =
    checkedFilteredData &&
    Object.keys(checkedFilteredData).length > 0 &&
    Object.keys(checkedFilteredData).filter(id => checkedFilteredData[id]).length ===
      Object.keys(checkedFilteredData).length;

  return (
    <div style={{ marginBottom: 24 }}>
      <Typography mb="16px" fontWeight="500" fontSize="14px">
        <FormattedMessage id="common.filter_by_values" defaultMessage="Filter by values:" />
      </Typography>
      <div className={styles.nameFilterLabels}>
        <div
          className={selectAllEnabled ? styles.enabledLabel : styles.disabledLabel}
          onClick={isSelectedAll ? handleUnSelectAll : handleSelectAll}
        >
          {isSelectedAll ? (
            <FormattedMessage id="common.unselect_all" defaultMessage="Unselect all" />
          ) : (
            <FormattedMessage id="common.select_all" defaultMessage="Select all" />
          )}
        </div>
        <div style={{ width: 4 }} />
        {'-'}
        <div style={{ width: 4 }} />
        <div className={clearEnabled ? styles.enabledLabel : styles.disabledLabel} onClick={handleClearColumnFilter}>
          <FormattedMessage id="common.clear" defaultMessage="Clear" />
        </div>
      </div>
      <Textbox
        placeholder={intl.formatMessage({ id: 'common.search_ellipsis', defaultMessage: 'Search...' })}
        onChange={handleSearchQueryChange}
        id="searchText"
        value={searchQuery}
        autoComplete="off"
        className={styles.input}
        mb="8px"
      />
      <div className={styles.filterListContainer}>
        {activeColumn &&
        filteredDisplayedData &&
        filteredDisplayedData.length &&
        searchQuery.length >= minQueryLength ? (
          <>
            {filteredDisplayedData.map(d => (
              <div key={d.id} className={styles.checkboxContainer} style={{ margin: 6 }}>
                <Checkbox
                  checked={checkedFilteredData ? Boolean(checkedFilteredData[d.id]) : false}
                  value={String(checkedFilteredData ? Boolean(checkedFilteredData[d.id]) : false)}
                  onChange={() => handleDataCheckChange(d)}
                  label={d[activeColumn] !== undefined ? String(d[activeColumn]) : ''}
                />
              </div>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );
};
